import { GlobalState } from 'app/store/main';
import axios, { AxiosInstance } from 'axios';
import { resetState } from 'modules/Authentication/store/slice';
import { path } from 'ramda';
import { toast } from 'react-toastify';
import { IApplicationContext } from '../context/types';
import { InvokeOptions } from './types';

class ApiService {
	isMockActive = false;
	axiosWithAuthorizationInterceptor: AxiosInstance = axios.create();
	constructor(private applicationContext: IApplicationContext) {
		this.configureAxiosInterceptors();
	}

	private async configureAxiosInterceptors() {
		const AUTH_ENPOINTS = this.applicationContext.getApiConfig().AUTH;
		const DISABLE_REFRESH_TOKEN_URLS = [
			AUTH_ENPOINTS.LOGIN.url,
			AUTH_ENPOINTS.RESET_PASSWORD_REQUEST.url,
			AUTH_ENPOINTS.RESET_PASSWORD.url,
			AUTH_ENPOINTS.VERIFY_RESET_PASSWORD_LINK.url,
		];
		// auth token header interceptor
		this.axiosWithAuthorizationInterceptor.interceptors.request.use(
			async config => {
				const authToken = this.applicationContext.authToken;
				if (authToken) {
					config.headers = { ...config.headers, Authorization: authToken };
				}
				return config;
			},
			error => {
				window.location.reload();
				Promise.reject(error);
			},
		);
		// refresh token interceptor
		this.axiosWithAuthorizationInterceptor.interceptors.response.use(
			res => res,
			async error => {
				const originalRequest = error.config;

				if (
					!DISABLE_REFRESH_TOKEN_URLS.includes(originalRequest.url) &&
					error?.response &&
					[401, 403].includes(error.response.status) &&
					!originalRequest._retry
				) {
					originalRequest._retry = true;
					try {
						const refreshToken = this.applicationContext.refreshToken;
						if (refreshToken) {
							await this.refreshAuthToken(refreshToken);
							return this.axiosWithAuthorizationInterceptor(originalRequest);
						} else {
							this.sessionExpiredRedirect();
						}
					} catch (err) {
						this.sessionExpiredRedirect();
						console.log('REFRESH-TOKEN::ERROR ', err);
					}
				}
				return Promise.reject(error);
			},
		);
	}

	private async sessionExpiredRedirect() {
		this.applicationContext.clearAutheticatedAdminUserData();
		GlobalState.getStore().dispatch(resetState({}));
		toast.info('Your session is expired, please login again!');
	}

	private async refreshAuthToken(refreshToken: string) {
		const {
			data: {
				data: { authToken, admin },
			},
		} = await axios({
			...this.applicationContext.getApiConfig().AUTH.REFRESH_TOKEN,
			data: { refreshToken },
		});
		this.applicationContext.setAdminUserData(admin);
		this.applicationContext.setAuthToken(authToken);
	}
	async invoke(endpoint: any, requestData: any, options?: InvokeOptions) {
		try {
			if (!endpoint.url || !endpoint.method) {
				return;
			}
			const invokeOptions = this.formatOptions(options || {});
			return this.callWS(endpoint, requestData, invokeOptions);
		} catch (error) {
			if (options?.shouldHandleError) {
				this.applicationContext.handleError(error);
			}
		}
	}

	private callWS(
		endpoint: any,
		requestData: any,
		invokeOptions?: InvokeOptions,
	) {
		return new Promise((resolve, reject) => {
			const invokeParams = this.buildRequest(
				endpoint,
				requestData,
				invokeOptions || {},
			);

			this.axiosWithAuthorizationInterceptor(invokeParams).then(
				response => {
					this.checkResponse(path(['data'], response), invokeOptions).then(
						(resultBody: any) => {
							resolve(resultBody);
						},
						(error: any) => {
							reject(error);
						},
					);
				},
				(error: any) => {
					if (invokeOptions?.shouldHandleError) {
						this.applicationContext.handleError(error.response);
					}
					reject(error);
				},
			);
		});
	}

	private buildRequest(
		endpoint: any,
		requestData: any,
		options: InvokeOptions,
	) {
		return {
			method: endpoint.method,
			url: endpoint.url + this.formatPathParams(options),
			data:
				!endpoint.method || endpoint.method !== 'GET' ? requestData : undefined,
			headers: this.getHeaders(),
		};
	}

	private checkResponse(responseBody: any, invokeOptions?: InvokeOptions) {
		return new Promise((resolve, reject) => {
			if (this.isSuccess(responseBody)) {
				resolve(responseBody);
			} else if (this.isError(responseBody)) {
				if (invokeOptions?.shouldHandleError) {
					this.applicationContext.handleError(responseBody);
				}
				reject(responseBody);
			} else if (this.isInfo(responseBody)) {
				this.applicationContext.handleInfo(responseBody);
			} else {
				resolve(responseBody);
			}
		});
	}

	private isSuccess(responseBody: any) {
		return (
			!this.isError(responseBody) &&
			!this.isWarning(responseBody) &&
			!this.isInfo(responseBody)
		);
	}

	private isInfo(responseBody: any) {
		return (
			path(['header'], responseBody) &&
			path(['header', 'messageType'], responseBody) === 'INFO'
		);
	}

	private isWarning(responseBody: any) {
		return path(['header', 'messageType'], responseBody) === 'WARNING';
	}

	private isError(responseBody: any) {
		return (
			path(['header'], responseBody) &&
			path(['header', 'messageType'], responseBody) === 'ERROR'
		);
	}

	private formatPathParams(options: InvokeOptions): string {
		// url = url/{reqPathParam}
		if (path(['reqPathParam'], options)) {
			return `/${options.reqPathParam}`;
		}
		// url = url?key1=pathParams[1]&key2=pathParams[2]...
		if (path(['pathParams'], options)) {
			const { pathParams } = options;
			let url = '?';
			const keys = Object.keys(pathParams);
			keys.forEach((key, index) => {
				url += `${key}=${encodeURIComponent(pathParams[key])}`;
				if (index !== keys.length - 1) {
					url += '&';
				}
			});
			return url;
		}
		return '';
	}

	private formatOptions(options: InvokeOptions): InvokeOptions {
		return {
			header: options.header || null,
			pathParams: options.pathParams || null,
			reqPathParam: options.reqPathParam || null,
			shouldHandleError: options.shouldHandleError || true,
			showLoader: options.showLoader || false,
			noExpSessionHandle: options.noExpSessionHandle || false,
			file: options.file || null,
		};
	}

	private getDeviceDataAsString(): string {
		try {
			return this.applicationContext.getDeviceDataAsString();
		} catch (e) {
			console.warn('ApiService::getDeviceDataAsString', e);
		}
		return '{}';
	}

	private getCurrentLanguage(): string {
		try {
			return this.applicationContext.getCurrentLanguage();
		} catch (e) {
			console.warn('ApiService::getCurrentLanguage', e);
		}
		return 'en';
	}

	private getHeaders() {
		const headers: any = {
			language: this.getCurrentLanguage(),
			Device: this.getDeviceDataAsString(),
		};
		return headers;
	}
}

export default ApiService;
